/*
 * gmx-glitter-picker.js — behaviour for snippets/gmx-glitter-picker.liquid.
 *
 * The glitter picker is a global component (product page + commission page),
 * so it carries its own script instead of being duplicated per page:
 *   - Clicking a swatch adds one of that shade, up to `data-max` total units.
 *   - Clicking a shade again adds another of the same shade (a shopper can
 *     pick "2x Magenta"), until the total hits `data-max`.
 *   - Once the total is at `data-max`, clicking a shade that already has at
 *     least one unit cycles it back out to zero, freeing up room. Shades with
 *     zero units stay disabled while full.
 *   - Chosen shades render as chips under the swatches, each with a small
 *     "−" button that removes one unit of that shade.
 *   - The names are written into the hidden `properties[...]` input as
 *     "Name" or "Name ×2" for multiples, comma-joined, e.g.
 *     "Magenta ×2, Cobalt Blue".
 *
 * Contract with the callers (assets/gmx-product.js's buy-box submit handler):
 *   the picker element (`[data-gmx-glitter-picker]`) gets a `.gmxValidate()`
 *   method attached. It returns an error string when the picker is required
 *   and empty, or null otherwise, and flags `.is-invalid` + scrolls to itself
 *   on failure. Callers query the picker fresh and call `.gmxValidate()`
 *   just before posting — no global object needed.
 *
 * Self-initializes for every picker on the page and re-initializes any new
 * one the theme editor adds (`shopify:section:load`). Idempotent per element.
 */
(function () {
  "use strict";

  function initGlitterPicker(picker) {
    var max = parseInt(picker.getAttribute("data-max"), 10) || 4;
    var swatches = Array.prototype.slice.call(picker.querySelectorAll("[data-gmx-glitter-swatch]"));
    var countEl = picker.querySelector("[data-gmx-glitter-count]");
    var chipsEl = picker.querySelector("[data-gmx-glitter-chips]");
    var valueEl = picker.querySelector("[data-gmx-glitter-value]");

    /* qty: shade name -> unit count. order: names in the order first picked,
       kept even after a shade drops to zero so a re-pick lands back in the
       same chip position instead of jumping to the end. */
    var qty = {};
    var order = [];

    function getQty(name) {
      return qty[name] || 0;
    }

    function setQty(name, next) {
      next = Math.max(0, next);
      qty[name] = next;
      if (next > 0 && order.indexOf(name) === -1) order.push(name);
    }

    function total() {
      var sum = 0;
      for (var name in qty) {
        if (Object.prototype.hasOwnProperty.call(qty, name)) sum += qty[name];
      }
      return sum;
    }

    function swatchFor(name) {
      return swatches.filter(function (sw) {
        return sw.getAttribute("data-name") === name;
      })[0];
    }

    function render() {
      var t = total();
      var full = t >= max;

      swatches.forEach(function (sw) {
        var name = sw.getAttribute("data-name");
        var count = getQty(name);
        var on = count > 0;
        sw.classList.toggle("is-on", on);
        sw.setAttribute("aria-pressed", on ? "true" : "false");
        sw.disabled = full && !on;
        sw.setAttribute("aria-label", count >= 2 ? name + ", " + count + " selected" : name);

        var badge = sw.querySelector("[data-gmx-glitter-badge]");
        if (badge) {
          if (count >= 2) {
            badge.textContent = "×" + count;
            badge.hidden = false;
          } else {
            badge.textContent = "";
            badge.hidden = true;
          }
        }
      });

      countEl.textContent = t + "/" + max + " selected";
      countEl.classList.toggle("is-full", full);

      chipsEl.innerHTML = "";
      order.forEach(function (name) {
        var count = getQty(name);
        if (count <= 0) return;

        var source = swatchFor(name);
        var chip = document.createElement("span");
        chip.className = "gmx-glitpick__chip";

        var dot = document.createElement("span");
        dot.className = "gmx-glitpick__chip-dot";
        dot.style.background = source ? source.getAttribute("data-hex") : "transparent";
        chip.appendChild(dot);

        var label = count >= 2 ? name + " ×" + count : name;
        chip.appendChild(document.createTextNode(label));

        var minus = document.createElement("button");
        minus.type = "button";
        minus.className = "gmx-glitpick__chip-remove";
        minus.setAttribute("aria-label", "Remove one " + name);
        minus.textContent = "−";
        minus.addEventListener("click", function () {
          setQty(name, getQty(name) - 1);
          picker.classList.remove("is-invalid");
          render();
        });
        chip.appendChild(minus);

        chipsEl.appendChild(chip);
      });

      // An empty property would still post, so the input only exists as a
      // posted field while there is a real selection.
      valueEl.value = order
        .filter(function (name) {
          return getQty(name) > 0;
        })
        .map(function (name) {
          var count = getQty(name);
          return count >= 2 ? name + " ×" + count : name;
        })
        .join(", ");
      valueEl.disabled = t === 0;
    }

    swatches.forEach(function (sw) {
      sw.addEventListener("click", function () {
        var name = sw.getAttribute("data-name");
        var count = getQty(name);
        var t = total();

        if (t < max) {
          setQty(name, count + 1);
        } else if (count > 0) {
          /* At the cap, tapping an already-chosen shade cycles it out so its
             room can go to something else, instead of doing nothing. */
          setQty(name, 0);
        }

        picker.classList.remove("is-invalid");
        render();
      });
    });

    render();

    /* GlitterKix's option was required under BCPO, so the picker can be too.
       `gmxValidate()` is what the PDP's add-to-cart handler calls before it
       posts (assets/gmx-product.js, initBuyBox). */
    picker.gmxValidate = function () {
      if (picker.getAttribute("data-required") !== "true") return null;
      if (total() > 0) return null;
      picker.classList.add("is-invalid");
      picker.scrollIntoView({ block: "center", behavior: "smooth" });
      return "Pick your glitter color" + (max > 1 ? "s" : "") + " to add this kit to your cart.";
    };
  }

  function init() {
    document.querySelectorAll("[data-gmx-glitter-picker]").forEach(function (picker) {
      if (picker.dataset.gmxGlitterReady) return;
      picker.dataset.gmxGlitterReady = "1";
      initGlitterPicker(picker);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  /* The theme editor re-renders a section in place. */
  document.addEventListener("shopify:section:load", init);
})();
